import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  useDomains,
  useServers,
  useSubdomains,
  useMonitors,
  useActivityLog,
} from "../hooks/queries";
import DashboardLayout from "../layouts/DashboardLayout";
import { Box, Typography, Stack, Button, Skeleton } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import { daysUntil, colorForDays } from "../utils/expiry";
import FlatCard from "../components/FlatCard";
import CountUp from "../components/CountUp";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";

// --- Spec palette (blue · light) — sourced from the shared design tokens -------
const BLUE = COLORS.primary;
const BLUE_DARK = COLORS.primaryDark;
const BLUE_SOFT = COLORS.primarySoft;
const INK = COLORS.ink;
const MUTED = COLORS.muted;
const FAINT = COLORS.faint;
const HAIRLINE = COLORS.hairline;
const TILE_BG = COLORS.tile;

// "5m ago" / "2h ago" / "3d ago" — compact relative time for the activity feed.
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 30) return `${dd}d ago`;
  return d.toLocaleDateString();
}

// Build the next-6-months expiry forecast buckets for the area chart.
function expiryForecast(domains) {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleString("default", { month: "short" }),
      count: 0,
    });
  }
  domains.forEach((dm) => {
    if (!dm.expiry) return;
    const e = new Date(dm.expiry);
    if (Number.isNaN(e.getTime())) return;
    const key = `${e.getFullYear()}-${e.getMonth()}`;
    const bucket = months.find((x) => x.key === key);
    if (bucket) bucket.count += 1;
  });
  return months;
}

function Dashboard() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // Show the logged-in user's name in the hero ("john" → "John"), falling back
  // to "Admin" before the profile loads.
  const displayName = currentUser?.username
    ? currentUser.username.charAt(0).toUpperCase() + currentUser.username.slice(1)
    : "Admin";

  // Each widget's data comes from its own cached React Query hook. They load in
  // parallel and fail independently — a failure on one doesn't blank the rest —
  // and the responses are shared with the other pages that use the same keys.
  const domainsQuery = useDomains();
  // Servers no longer have a stat tile, but the query stays — the alerts panel
  // below still flags servers renewing within 30 days.
  const serversQuery = useServers();
  const subdomainsQuery = useSubdomains();
  const monitorsQuery = useMonitors();
  const activityQuery = useActivityLog();

  // The list endpoints return a bare array with no params; normalise defensively
  // in case a paginated { rows } shape comes back.
  const asArray = (data) =>
    Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
  const domains = asArray(domainsQuery.data);
  const servers = asArray(serversQuery.data);
  const subdomains = asArray(subdomainsQuery.data);
  const monitors = asArray(monitorsQuery.data);
  const activity = asArray(activityQuery.data);

  // Show skeletons until the first load of every widget has settled — matches
  // the old "setLoading(false) after all settle" behaviour.
  const loading =
    domainsQuery.isLoading ||
    serversQuery.isLoading ||
    subdomainsQuery.isLoading ||
    monitorsQuery.isLoading ||
    activityQuery.isLoading;

  // --- Derived figures -----------------------------------------------------
  const totalDomains = domains.length;
  // Anything not "Expired" (Active, Near Expiry, New, …) counts as active, so
  // Active + Expired always equals Total Domains.
  const expiredDomains = domains.filter((d) => d.status === "Expired").length;
  const activeDomains = totalDomains - expiredDomains;
  const totalSubdomains = subdomains.length;

  // Domains expiring within the next 30 days (not yet overdue).
  const expiring30 = useMemo(
    () =>
      domains.filter((d) => {
        const n = daysUntil(d.expiry);
        return n !== null && n >= 0 && n <= 30;
      }).length,
    [domains]
  );

  const mon = useMemo(() => {
    const up = monitors.filter((m) => m.isUp === true).length;
    const down = monitors.filter((m) => m.isUp === false).length;
    const responded = monitors.filter(
      (m) => m.isUp === true && typeof m.responseTimeMs === "number"
    );
    const avgResponse = responded.length
      ? Math.round(
          responded.reduce((sum, m) => sum + m.responseTimeMs, 0) /
            responded.length
        )
      : null;
    const sslChecked = monitors.filter((m) => m.sslValid !== null);
    const sslValid = sslChecked.filter((m) => m.sslValid === true).length;
    const sslSoon = monitors.filter(
      (m) => typeof m.sslDaysLeft === "number" && m.sslDaysLeft >= 0 && m.sslDaysLeft <= 30
    ).length;
    return {
      monitored: monitors.length,
      up,
      down,
      avgResponse,
      sslValid,
      sslChecked: sslChecked.length,
      sslSoon,
    };
  }, [monitors]);

  // Portfolio health score — average of whatever signals we have (active ratio,
  // uptime ratio, SSL validity ratio). 0–100.
  const healthScore = useMemo(() => {
    const parts = [];
    if (totalDomains) parts.push(activeDomains / totalDomains);
    if (mon.monitored) parts.push(mon.up / mon.monitored);
    if (mon.sslChecked) parts.push(mon.sslValid / mon.sslChecked);
    if (!parts.length) return 100;
    return Math.round((parts.reduce((a, b) => a + b, 0) / parts.length) * 100);
  }, [totalDomains, activeDomains, mon]);

  const healthHex =
    healthScore >= 85 ? "#16A34A" : healthScore >= 60 ? "#D97706" : "#DC2626";
  const healthLabel =
    healthScore >= 85 ? "Excellent" : healthScore >= 60 ? "Needs attention" : "Critical";

  const chartData = [
    { name: "Active", value: activeDomains },
    { name: "Expired", value: expiredDomains },
  ];
  const CHART_COLORS = ["#16A34A", "#DC2626"];

  const forecast = useMemo(() => expiryForecast(domains), [domains]);

  const domainsById = useMemo(() => {
    const map = new Map();
    domains.forEach((d) => map.set(d.id, d.name));
    return map;
  }, [domains]);

  const recentActivity = useMemo(() => activity.slice(0, 6), [activity]);

  // --- Needs attention -----------------------------------------------------
  // A single prioritised list of items that need action: down monitors,
  // overdue/expiring domains, SSL expiring soon, and servers renewing soon.
  const needsAttention = useMemo(() => {
    const items = [];

    // Down domains (from the monitor watcher).
    monitors
      .filter((m) => m.isUp === false)
      .forEach((m) => {
        items.push({
          key: `down-${m.domainId ?? m.id}`,
          icon: "down",
          hex: "#DC2626",
          bg: "rgba(220, 38, 38, 0.10)",
          title: domainsById.get(m.domainId) || m.domain || "Domain",
          sub: "Site is down",
          badge: "Down",
          sortDays: -1000,
          to: "/domain-monitors?filter=down",
        });
      });

    // Overdue / expiring domains (≤30 days).
    domains.forEach((d) => {
      const n = daysUntil(d.expiry);
      if (n === null || n > 30) return;
      const band = colorForDays(n);
      items.push({
        key: `dom-${d.id}`,
        icon: "expiry",
        hex: band.hex,
        bg: band.bg,
        title: d.name,
        sub: n < 0 ? `Expired ${Math.abs(n)}d ago` : `Expires in ${n}d`,
        badge: n < 0 ? "Expired" : band.label,
        sortDays: n,
        to: `/domains/${d.id}`,
      });
    });

    // SSL expiring soon (≤30 days).
    monitors.forEach((m) => {
      if (typeof m.sslDaysLeft !== "number" || m.sslDaysLeft < 0 || m.sslDaysLeft > 30)
        return;
      const band = colorForDays(m.sslDaysLeft);
      items.push({
        key: `ssl-${m.domainId ?? m.id}`,
        icon: "ssl",
        hex: band.hex,
        bg: band.bg,
        title: domainsById.get(m.domainId) || m.domain || "Domain",
        sub: `SSL expires in ${m.sslDaysLeft}d`,
        badge: "SSL",
        sortDays: m.sslDaysLeft,
        to: "/domain-monitors",
      });
    });

    // Servers renewing soon (≤30 days).
    servers.forEach((s) => {
      const n = daysUntil(s.expiry);
      if (n === null || n > 30) return;
      const band = colorForDays(n);
      items.push({
        key: `srv-${s.id}`,
        icon: "server",
        hex: band.hex,
        bg: band.bg,
        title: s.name || "Server",
        sub: n < 0 ? `Renewal overdue ${Math.abs(n)}d` : `Renews in ${n}d`,
        badge: "Server",
        sortDays: n,
        to: "/servers",
      });
    });

    return items.sort((a, b) => a.sortDays - b.sortDays).slice(0, 6);
  }, [domains, servers, monitors, domainsById]);

  // --- Stat cards (5, per spec) -------------------------------------------
  const stats = [
    {
      label: "Total Domains",
      value: totalDomains,
      icon: <LanguageIcon />,
      accent: BLUE,
      trend: totalDomains > 0 ? "All registered" : "Get started",
      to: "/domains",
    },
    {
      label: "Active",
      value: activeDomains,
      icon: <CheckCircleOutlinedIcon />,
      accent: "#16A34A",
      trend:
        totalDomains > 0
          ? `${Math.round((activeDomains / totalDomains) * 100)}% healthy`
          : "—",
      to: "/domains?filter=active",
    },
    {
      label: "Down / Issues",
      value: mon.down,
      icon: <CloudOffOutlinedIcon />,
      accent: "#DC2626",
      trend: mon.down > 0 ? "Needs attention" : "All up",
      to: "/domain-monitors?filter=down",
    },
    {
      label: "Expiring (30d)",
      value: expiring30,
      icon: <AccessTimeIcon />,
      accent: "#D97706",
      trend: expiring30 > 0 ? "Renew soon" : "Nothing soon",
      to: "/expiry-alerts",
    },
    {
      label: "Subdomains",
      value: totalSubdomains,
      icon: <AccountTreeOutlinedIcon />,
      accent: BLUE_DARK,
      trend: totalSubdomains > 0 ? "Across all domains" : "None yet",
      to: "/domains",
    },
  ];

  // --- Live monitoring tiles ----------------------------------------------
  const monTiles = [
    {
      label: "Online",
      value: mon.up,
      suffix: mon.monitored ? ` / ${mon.monitored}` : "",
      icon: <CloudDoneOutlinedIcon />,
      hex: "#16A34A",
      to: "/domain-monitors?filter=up",
    },
    {
      label: "Offline",
      value: mon.down,
      icon: <CloudOffOutlinedIcon />,
      hex: mon.down > 0 ? "#DC2626" : FAINT,
      to: "/domain-monitors?filter=down",
    },
    {
      label: "Avg Response",
      value: mon.avgResponse,
      display: mon.avgResponse === null ? "—" : `${mon.avgResponse}`,
      unit: mon.avgResponse === null ? "" : "ms",
      icon: <SpeedOutlinedIcon />,
      hex: BLUE,
      to: "/domain-monitors",
    },
    {
      label: "SSL Valid",
      value: mon.sslValid,
      suffix: mon.sslChecked ? ` / ${mon.sslChecked}` : "",
      sub: mon.sslSoon > 0 ? `${mon.sslSoon} expiring ≤30d` : null,
      icon: <GppGoodOutlinedIcon />,
      hex: "#0891B2",
      to: "/domain-monitors",
    },
  ];

  const sectionTitle = (icon, title, subtitle, action, titleAction) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 2.5 }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        {icon}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 16, fontWeight: 700, color: INK }}>
              {title}
            </Typography>
            {titleAction}
          </Stack>
          {subtitle && (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {action}
    </Stack>
  );

  // Consistent "Details / View all" link button (spec blue).
  const linkButton = (label, to) => (
    <Button
      onClick={() => navigate(to)}
      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
      sx={{
        ml: "auto",
        mr: -1,
        flexShrink: 0,
        textTransform: "none",
        fontSize: 12.5,
        fontWeight: 600,
        color: BLUE,
        "&:hover": { background: BLUE_SOFT },
      }}
    >
      {label}
    </Button>
  );

  return (
    <DashboardLayout>
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Hero / Welcome banner (gradient is reserved for the hero per spec) */}
        <Box
          sx={{
            position: "relative",
            background: `linear-gradient(150deg, ${BLUE_DARK} 0%, ${BLUE} 70%, #60A5FA 100%)`,
            borderRadius: "20px",
            p: { xs: 3, md: 4 },
            mb: 3.5,
            overflow: "hidden",
            color: "#fff",
            boxShadow: "0 12px 30px rgba(29, 78, 216, 0.25)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ md: "center" }}
            sx={{ position: "relative", zIndex: 1, justifyContent: "space-between" }}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 5,
                    background: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#4ade80",
                    }}
                  />
                  <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.5px" }}>
                    Live · {mon.monitored} monitored
                  </Typography>
                </Box>
              </Stack>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: { xs: 26, md: 33 },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: "-0.5px",
                  mb: 1,
                }}
              >
                Welcome back, {displayName}
              </Typography>
              <Typography sx={{ fontSize: 14, opacity: 0.92, maxWidth: 540 }}>
                Manage all your domains and servers in one place. Track expiries,
                monitor uptime, and stay ahead of renewals.
              </Typography>
            </Box>

            <Stack
              direction="row"
              sx={{
                display: { xs: "flex", md: "block" },
                flexDirection: { xs: "column", md: "row" },
                width: { xs: "100%", md: "auto" },
                gap: { xs: 1.5 },
                "& > .MuiButtonBase-root:not(:first-of-type)": { ml: { md: 1.5 } },
              }}
            >
              <Button
                onClick={() => navigate("/domains?add=1")}
                startIcon={<AddIcon />}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1.1,
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.25)",
                  "&:hover": { background: "rgba(255,255,255,0.28)" },
                }}
              >
                Add Domain
              </Button>
              <Button
                onClick={() => navigate("/add-server")}
                startIcon={<AddIcon />}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1.1,
                  borderRadius: "10px",
                  background: "#fff",
                  color: BLUE_DARK,
                  "&:hover": {
                    background: "#f3f4f6",
                    boxShadow: "0 8px 22px rgba(0,0,0,0.15)",
                  },
                }}
              >
                Add Server
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Stat cards — 5 KPIs (count-up only here) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <FlatCard key={i} sx={{ p: 3 }}>
                  <Skeleton variant="rounded" width={38} height={38} sx={{ mb: 1.5 }} />
                  <Skeleton variant="text" width="50%" height={44} />
                  <Skeleton variant="text" width="70%" />
                </FlatCard>
              ))
            : stats.map((stat) => (
                <FlatCard
                  key={stat.label}
                  hover
                  accent={stat.accent}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(stat.to)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(stat.to);
                    }
                  }}
                  sx={{ p: 3 }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 1, gap: "10px", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        background: `${stat.accent}14`,
                        color: stat.accent,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "& .MuiSvgIcon-root": { fontSize: 21 },
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Stack>

                  <CountUp
                    value={stat.value}
                    style={{
                      fontSize: 34,
                      fontWeight: 800,
                      color: INK,
                      lineHeight: 1.1,
                      letterSpacing: "-1px",
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: MUTED,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mt: 0.75,
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                    <TrendingUpIcon sx={{ fontSize: 14, color: stat.accent }} />
                    <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 500 }}>
                      {stat.trend}
                    </Typography>
                  </Stack>
                </FlatCard>
              ))}
        </Box>

        {/* Needs attention + Recent activity */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          {/* Needs attention */}
          <FlatCard sx={{ p: 3 }}>
            {sectionTitle(
              <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: "#D97706" }} />,
              "Needs Attention",
              "Down sites, expiring domains, SSL & server renewals",
              linkButton("View all", "/expiry-alerts")
            )}

            {loading ? (
              <Stack spacing={1}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={60} />
                ))}
              </Stack>
            ) : needsAttention.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center", color: FAINT }}>
                <CheckCircleOutlinedIcon sx={{ fontSize: 36, mb: 1, color: "#16A34A" }} />
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 14 }}>
                  All clear — nothing needs attention
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1} sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
                {needsAttention.map((item) => (
                  <Stack
                    key={item.key}
                    onClick={() => item.to && navigate(item.to)}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${HAIRLINE}`,
                      background: TILE_BG,
                      cursor: "pointer",
                      transition: "border-color 0.15s ease, transform 0.15s ease",
                      "&:hover": { borderColor: item.hex, transform: "translateX(2px)" },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          background: item.bg,
                          color: item.hex,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& .MuiSvgIcon-root": { fontSize: 18 },
                        }}
                      >
                        {item.icon === "down" ? (
                          <CloudOffOutlinedIcon />
                        ) : item.icon === "ssl" ? (
                          <GppGoodOutlinedIcon />
                        ) : item.icon === "server" ? (
                          <DnsOutlinedIcon />
                        ) : (
                          <AccessTimeIcon />
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: INK,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                          {item.sub}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: 1.4,
                        color: item.hex,
                        background: item.bg,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "8px",
                        flexShrink: 0,
                        ml: 1,
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {item.badge}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </FlatCard>

          {/* Recent activity feed */}
          <FlatCard sx={{ p: 3 }}>
            {sectionTitle(
              <HistoryOutlinedIcon sx={{ fontSize: 20, color: BLUE }} />,
              "Recent Activity",
              "Latest changes across domains",
              linkButton("View all", "/activity-log")
            )}

            {loading ? (
              <Stack spacing={1.5}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} variant="text" height={36} />
                ))}
              </Stack>
            ) : recentActivity.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center", color: FAINT }}>
                <HistoryOutlinedIcon sx={{ fontSize: 36, mb: 1 }} />
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 14 }}>No activity recorded yet</Typography>
              </Box>
            ) : (
              <Stack spacing={0} sx={{ maxHeight: 320, overflowY: "auto" }}>
                {recentActivity.map((a, idx) => (
                  <Stack
                    key={a.id}
                    direction="row"
                    spacing={1.5}
                    sx={{
                      py: 1.25,
                      borderBottom:
                        idx < recentActivity.length - 1
                          ? `1px solid ${HAIRLINE}`
                          : "none",
                    }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: BLUE,
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 13.5, color: INK, lineHeight: 1.4 }}>
                        {a.activityText}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: BLUE }}>
                          {domainsById.get(a.domainId) || "Domain"}
                        </Typography>
                        <Box
                          sx={{
                            width: 3,
                            height: 3,
                            borderRadius: "50%",
                            background: FAINT,
                            flexShrink: 0,
                            alignSelf: "center",
                            position: "relative",
                            top: "1px",
                          }}
                        />
                        <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                          {timeAgo(a.createdAt)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </FlatCard>
        </Box>

        {/* Live monitoring strip + health gauge */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.7fr 1fr" },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          <FlatCard sx={{ p: 3 }}>
            {sectionTitle(
              <BoltOutlinedIcon sx={{ fontSize: 20, color: BLUE }} />,
              "Live Monitoring",
              "Real-time uptime & SSL from the watcher",
              linkButton("Details", "/domain-monitors")
            )}

            {mon.monitored === 0 && !loading ? (
              <Box sx={{ py: 4, textAlign: "center", color: FAINT }}>
                <BoltOutlinedIcon sx={{ fontSize: 34, mb: 1 }} />
                <Typography sx={{ fontSize: 13.5 }}>
                  No monitoring data yet — the watcher fills this in on its next run.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                {monTiles.map((t) => (
                  <Box
                    key={t.label}
                    onClick={() => t.to && navigate(t.to)}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: `1px solid ${HAIRLINE}`,
                      background: TILE_BG,
                      cursor: t.to ? "pointer" : "default",
                      transition: "border-color 0.15s ease, transform 0.15s ease",
                      "&:hover": t.to
                        ? { borderColor: t.hex, transform: "translateY(-2px)" }
                        : {},
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        mb: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: t.hex,
                        background: `${t.hex}1f`,
                        "& .MuiSvgIcon-root": { fontSize: 19 },
                      }}
                    >
                      {t.icon}
                    </Box>
                    <Stack direction="row" alignItems="baseline" spacing={0.4}>
                      {loading ? (
                        <Skeleton variant="text" width={40} height={30} />
                      ) : t.display !== undefined ? (
                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px" }}>
                          {t.display}
                          {t.unit && (
                            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: MUTED, ml: 0.3 }}>
                              {t.unit}
                            </Typography>
                          )}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px" }}>
                          {t.value}
                          {t.suffix && (
                            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: MUTED }}>
                              {t.suffix}
                            </Typography>
                          )}
                        </Typography>
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 600, mt: 0.25 }}>
                      {t.label}
                    </Typography>
                    {t.sub && (
                      <Typography sx={{ fontSize: 11, color: "#D97706", fontWeight: 600, mt: 0.25 }}>
                        {t.sub}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </FlatCard>

          {/* Health score radial gauge */}
          <FlatCard sx={{ p: 3, display: "flex", flexDirection: "column" }}>
            {sectionTitle(
              <GppGoodOutlinedIcon sx={{ fontSize: 20, color: healthHex }} />,
              "Portfolio Health",
              "Active · uptime · SSL combined"
            )}
            <Box sx={{ position: "relative", flex: 1, minHeight: 190 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  innerRadius="72%"
                  outerRadius="100%"
                  data={[{ value: healthScore, fill: healthHex }]}
                  startAngle={90}
                  endAngle={-270}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar background={{ fill: "#EEF2F8" }} dataKey="value" cornerRadius={20} />
                </RadialBarChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <Typography sx={{ fontSize: 40, fontWeight: 800, color: INK, lineHeight: 1, letterSpacing: "-1.5px" }}>
                  {healthScore}%
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: healthHex, mt: 0.5 }}>
                  {healthLabel}
                </Typography>
              </Box>
            </Box>
          </FlatCard>
        </Box>

        {/* Charts row: status donut + expiry forecast */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" },
            gap: 2.5,
          }}
        >
          <FlatCard sx={{ p: 3 }}>
            {sectionTitle(
              <LanguageIcon sx={{ fontSize: 20, color: BLUE }} />,
              "Domain Status",
              "Active vs expired breakdown"
            )}
            <Box sx={{ width: "100%", height: 250 }}>
              {totalDomains === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: FAINT,
                    fontSize: 14,
                    gap: 1,
                  }}
                >
                  <LanguageIcon sx={{ fontSize: 36 }} />
                  No domains yet
                </Box>
              ) : (
                <Box sx={{ position: "relative", height: "100%" }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        innerRadius={68}
                        outerRadius={98}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={CHART_COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "#fff",
                          border: `1px solid ${HAIRLINE}`,
                          borderRadius: 10,
                          fontSize: 13,
                          color: INK,
                          boxShadow: "0 8px 24px rgba(16,24,40,.10)",
                        }}
                        itemStyle={{ color: INK }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography sx={{ fontSize: 30, fontWeight: 800, color: INK, lineHeight: 1 }}>
                      {totalDomains}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>Total</Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Stack direction="row" justifyContent="center" spacing={3} sx={{ mt: 1 }}>
              {chartData.map((c, i) => (
                <Stack key={c.name} direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: CHART_COLORS[i] }} />
                  <Typography sx={{ fontSize: 12.5, color: MUTED, fontWeight: 600 }}>
                    {c.name} · {c.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </FlatCard>

          {/* Expiry forecast area chart */}
          <FlatCard sx={{ p: 3 }}>
            {sectionTitle(
              <TrendingUpIcon sx={{ fontSize: 20, color: BLUE }} />,
              "Expiry Forecast",
              "Domains expiring over the next 6 months"
            )}
            <Box sx={{ width: "100%", height: 250 }}>
              <ResponsiveContainer>
                <AreaChart data={forecast} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="expiryFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BLUE} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={HAIRLINE} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: MUTED }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: MUTED }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: `1px solid ${HAIRLINE}`,
                      borderRadius: 10,
                      fontSize: 13,
                      color: INK,
                      boxShadow: "0 8px 24px rgba(16,24,40,.10)",
                    }}
                    labelStyle={{ color: INK, fontWeight: 700 }}
                    itemStyle={{ color: INK }}
                    formatter={(v) => [`${v} domain${v === 1 ? "" : "s"}`, "Expiring"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={BLUE}
                    strokeWidth={2.5}
                    fill="url(#expiryFill)"
                    dot={{ r: 3, fill: BLUE, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </FlatCard>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default Dashboard;
